import React, { cloneElement, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';

import type { Node } from '@atlaskit/editor-prosemirror/model';
import { ExpandBodyProvider } from '@atlaskit/renderer/expand-body';
import type { ExpandBody } from '@atlaskit/renderer/expand-body';

import { isExperimentEnabled } from '@confluence/feature-experiments';

import { useLightNodeRef } from './LightNodeObserver.ts';
import type { LightNodeProps } from './LightNodeObserver.ts';
import type { LightSubtreeScheduler } from './LightSubtreeScheduler.ts';

type LightSubtreeResource = {
	subtreeId: number;
	node: Node;
	light: ReactElement<LightNodeProps>;
	prepareFull?: () => Promise<void>;
	serializeFull: () => ReactElement | null;
};

export function LightSubtree({
	resource,
	scheduler,
}: {
	resource: LightSubtreeResource;
	scheduler: LightSubtreeScheduler;
}): ReactElement | null {
	// Stays full once activated, even if its ADF changes; the page's revision key owns document resets.
	const [active, setActive] = useState(false);
	// The outermost body, so nothing above these expands is collapsed.
	const [expandBody] = useState<ExpandBody | null>(() =>
		isExperimentEnabled('cc_light_mode_table_virtualization')
			? { revealed: true, openWithAncestors: () => {}, revealedByFind: new WeakSet() }
			: null,
	);
	const activate = useCallback(() => setActive(true), []);
	const holdsTable = useMemo(() => containsTable(resource.node), [resource.node]);
	const observe = useCallback(
		(element: HTMLElement) => scheduler.observe(resource.subtreeId, element, holdsTable),
		[resource.subtreeId, scheduler, holdsTable],
	);
	const lightNodeRef = useLightNodeRef(active ? undefined : observe);
	useEffect(
		() => scheduler.mount(resource.subtreeId, activate, resource.prepareFull),
		[scheduler, resource.subtreeId, activate, resource.prepareFull],
	);
	const tree = useMemo(() => {
		if (!active) {
			// Descendants keep their light components but receive no observer ref.
			return cloneElement(resource.light, { lightNodeRef });
		}
		return resource.serializeFull();
	}, [active, resource, lightNodeRef]);
	useEffect(() => {
		if (active) {
			scheduler.acknowledge(resource.subtreeId);
		}
	}, [active, scheduler, resource.subtreeId]);
	// The provider stays mounted across the swap, so expands come back as the reader or find left them.
	return expandBody ? <ExpandBodyProvider value={expandBody}>{tree}</ExpandBodyProvider> : tree;
}

function containsTable(node: Node): boolean {
	if (node.type.name === 'table') {
		return true;
	}
	let found = false;
	node.descendants((descendant) => {
		found ||= descendant.type.name === 'table';
		return !found;
	});
	return found;
}
